import { LoadingButton } from '@mui/lab';
import {
    Box,
    Button,
    Divider,
    FormControlLabel,
    Radio,
    RadioGroup,
    Tooltip,
    Typography
} from '@mui/material';
import { isDefined } from 'lib/typeguards';
import React from 'react';
import { Row } from 'ui/Flex';
import { MuiModal } from 'ui/MuiModal';

type TriggeredNotificationsDeletePendingModalProps = {
    title: React.ReactNode;
    open: boolean;
    onClose?: () => void;
    onConfirm: (deleteNotifications: boolean) => void;
    loading?: boolean;
};

const VALUES_LIST = ['yes', 'no'];
function isTriggeredNotificationsDeleteOption(value: any): value is 'yes' | 'no' | undefined {
    return !isDefined(value) || VALUES_LIST.includes(value);
}

const TriggeredNotificationsDeletePendingModalContent: React.FC<
    Omit<TriggeredNotificationsDeletePendingModalProps, 'open'>
> = ({ title, loading, onConfirm, onClose }: TriggeredNotificationsDeletePendingModalProps) => {
    const [value, setValue] = React.useState<'yes' | 'no' | undefined>();
    const handleRadioSelect = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setValue(isTriggeredNotificationsDeleteOption(e.target.value) ? e.target.value : undefined);
    }, []);
    const handleSubmit = React.useCallback(() => {
        if (isDefined(value)) {
            onConfirm(value === 'yes');
        }
    }, [onConfirm, value]);
    return (
        <>
            <Box display="flex" alignItems="center">
                <Typography variant="h5" color="primary">
                    {title}
                </Typography>
            </Box>
            <Divider sx={{ my: 2, mx: 0 }} />
            <Typography sx={{ whiteSpace: 'pre-wrap' }} variant="body1">
                Would you also like to delete all pending notifications that were scheduled with this
                campaign? Please be aware, once deleted, there is no way to recover them.
            </Typography>
            <RadioGroup value={value} onChange={handleRadioSelect}>
                <FormControlLabel
                    value="yes"
                    control={<Radio />}
                    label={
                        <>
                            Yes, <b>delete</b>
                        </>
                    }
                />
                <FormControlLabel
                    value="no"
                    control={<Radio />}
                    label={
                        <>
                            No, <b>keep</b>
                        </>
                    }
                />
            </RadioGroup>
            <Divider sx={{ my: 2, mx: 0 }} />
            <Row align="flex-end" flex={1} gutter>
                <Button variant="outlined" color="primary" disabled={loading} onClick={onClose}>
                    Cancel
                </Button>
                {!isDefined(value) && (
                    <Tooltip title="Please select one of the options above" placement="top">
                        <Box>
                            <LoadingButton
                                loading={loading}
                                disabled
                                variant="contained"
                                color="primary"
                                onClick={handleSubmit}
                            >
                                Confirm
                            </LoadingButton>
                        </Box>
                    </Tooltip>
                )}
                {isDefined(value) && (
                    <LoadingButton
                        loading={loading}
                        disabled={loading || !isDefined(value)}
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                    >
                        Confirm
                    </LoadingButton>
                )}
            </Row>
        </>
    );
};

export const TriggeredNotificationsDeletePendingModal: React.FC<
    TriggeredNotificationsDeletePendingModalProps
> = ({ open, ...rest }) => (
    <MuiModal open={open} onClose={rest.onClose}>
        <TriggeredNotificationsDeletePendingModalContent {...rest} />
    </MuiModal>
);
